<template>
	<div class="landing-page">
		<!-- Header Section -->
		<CustomSection
			wave
			style="background-color: #1a1a1a; margin-top: 0"
			image="https://assets.livereader.com/asset/ZS8JL0vkkbfXaMQ8.png"
		>
			<div class="header-div">
				<v-row class="header-row">
					<v-col cols="12" md="7">
						<div class="header-content">
							<h1 class="headline">Berufsberatung</h1>
							<p class="header-text">
								Mensch-KI-Kollaboration in einer Berufsberatung: Ein Modell für die Zukunft?
							</p>
						</div>
					</v-col>
				</v-row>
			</div>
		</CustomSection>

		<!-- Cases Section -->
		<CustomSection style="background-color: rgb(var(--v-theme-background))">
			<div class="section-div">
				<v-row>
					<h1 class="title">Wählen Sie einen Fall</h1>
					<v-col cols="12">
						<!-- Access Setup Loading State -->
						<div v-if="settingUpAccess" class="loading-section">
							<v-progress-circular indeterminate color="#80cbc4" size="64"></v-progress-circular>
							<p class="loading-text">Bereite Zugang vor...</p>
						</div>

						<!-- Cases Loading State -->
						<div v-else-if="loadingCases" class="loading-section">
							<v-progress-circular indeterminate color="#80cbc4" size="64"></v-progress-circular>
							<p class="loading-text">Lade verfügbare Fälle...</p>
						</div>

						<!-- Cases Grid -->
						<v-row v-else-if="cases.length > 0 && hasEditorAccess" class="cases-row">
							<v-col v-for="case_ in cases" :key="case_.botId" cols="12" sm="12" md="6" lg="4">
								<v-card
									v-if="personalDataspaceId"
									class="case-card"
									elevation="10"
									variant="tonal"
									:disabled="loading"
									@click="selectCase(case_, personalDataspaceId)"
								>
									<v-card-title class="case-title">
										<b>{{ case_.botName }}</b>
									</v-card-title>
									<v-card-text class="case-text">
										<div class="case-icon-wrapper">
											<v-icon size="48" color="#80cbc4">mdi-account-circle</v-icon>
										</div>
										{{ getCaseDescription(case_.botId) }}
									</v-card-text>
									<v-card-actions class="case-actions">
										<v-btn
											color="#80cbc4"
											variant="elevated"
											block
											:loading="loading && selectedCaseId === case_.botId"
											class="case-button"
										>
											Fall starten
										</v-btn>
									</v-card-actions>
								</v-card>
							</v-col>
						</v-row>

						<!-- No Access -->
						<div v-else-if="!hasEditorAccess && !settingUpAccess" class="no-cases-section">
							<v-icon size="64" color="#ff7043" class="mb-4">mdi-lock</v-icon>
							<p class="no-cases-text">Kein Zugang zu Beratungsfällen</p>
							<p class="no-cases-text">Sie benötigen Editor-Rechte für diese Domain</p>
						</div>

						<!-- No Cases Available -->
						<div v-else-if="cases.length === 0 && hasEditorAccess" class="no-cases-section">
							<v-icon size="64" color="#80cbc4" class="mb-4">mdi-robot-off</v-icon>
							<p class="no-cases-text">Keine Beratungsfälle verfügbar</p>
						</div>
					</v-col>
				</v-row>
			</div>
		</CustomSection>

		<!-- Info Text Section -->
		<CustomSection style="background-color: rgb(var(--v-theme-background))">
			<div class="section-div">
				<v-row>
					<v-col cols="12">
						<div class="description-section">
							<p class="description-text">
								Wie sieht Berufsberatung aus, wenn Mensch und KI ihre Stärken bündeln? Dieser
								Demonstrator lädt dazu ein, genau das zu erleben. Eine sprachbasierte KI analysiert live
								Gesprächsinhalte, erkennt Werte, Interessen und Einstellungen – und verknüpft sie mit
								über 3.500 Berufseinträgen aus BERUFENET. Das Ergebnis ist eine interaktive, visuell
								unterstützte Beratung, bei der Berufsberater:innen sich auf den versierten Umgang mit
								einer ratsuchenden Person konzentrieren - die KI fokussiert sich hingegen auf die
								Analyse umfangreicher Datenmengen, um sich mit gezielten Hinweisen einzuschalten. Durch
								ein ausgeklügeltes Design der Interaktionen zwischen Mensch und KI bleiben
								Berufsberater:innen jedoch zu jedem Zeitpunkt in der Führungsrolle. Probieren Sie es
								jetzt aus und entdecken Sie das Potenzial symbiotischer Zusammenarbeit zwischen Mensch
								und KI.
							</p>
						</div>
					</v-col>
				</v-row>
			</div>
		</CustomSection>

		<!-- Logos Section -->
		<CustomSection style="background-color: rgb(var(--v-theme-background))">
			<div class="section-div">
				<v-row>
					<v-col cols="12">
						<div class="logos-section">
							<div class="logo-container">
								<img src="/dfki_Logo_vz.svg" alt="DFKI Logo" class="logo dfki-logo" />
							</div>
							<div class="logo-container hdba-container" style="width: 550px">
								<img src="/logo_hdba_left.svg" alt="HDBA Logo Left" class="logo hdba-logo" />
								<img src="/logo_hdba_right.svg" alt="HDBA Logo Right" class="logo hdba-logo" />
							</div>
						</div>
					</v-col>
				</v-row>
			</div>
		</CustomSection>

		<!-- Footer Wave -->
		<CustomSection
			class="last-segment"
			wave
			wave-color="#80cbc4"
			style="background-color: rgb(var(--v-theme-background))"
		>
			<div style="height: 50px"></div>
		</CustomSection>

		<Footer />

		<!-- Access Setup Snackbar -->
		<v-snackbar v-model="showAccessSnackbar" :color="accessSnackbarType" :timeout="4000" location="top">
			{{ accessSnackbarMessage }}
			<template v-slot:actions>
				<v-btn color="white" variant="text" @click="showAccessSnackbar = false"> Schließen </v-btn>
			</template>
		</v-snackbar>
	</div>
</template>

<script setup lang="ts">
import { ref, onMounted, onUnmounted, computed } from "vue";
import { useRouter } from "vue-router";
import { useDomainStore } from "../../../store/domain";
import { useInstanceWorkspacesStore } from "../../../store/i-workspace";
import { useInstanceDataspacesStore } from "../../../store/i-dataspace";
import { useUserStore } from "../../../store/user";
import { api as alternisAPI } from "@lr-notitia/notitia-alternis/client";
import { api as operatorAPI } from "@lr-notitia/notitia-operator/client";
import { UserRole } from "@lr-notitia/notitia-operator/schemas";
import CustomSection from "../components/CustomSection.vue";
import Footer from "../components/Footer.vue";

const DOMAIN_ID = "49eaae15-7af8-4986-a2cc-4b12cbaf5313"; // Berufsberatung .dev and .world
const FALLBACK_DATASPACE_VERSION = "2.0.2"; // Used if the latest release version cannot be determined
const TEMPALTE_DATASPACE_ID = "71120130-7483-4554-87c7-82b907ec69b5"; // General Template Dataspace
const TEMPLATE_WORKSPACE_ID = "68b203c7-56ff-45c1-9e14-49426ebef48b"; // Workspace template for Berufsberatung cases

const router = useRouter();
const domainStore = useDomainStore();
const instanceWorkspacesStore = useInstanceWorkspacesStore();
const instanceDataspacesStore = useInstanceDataspacesStore();
const userStore = useUserStore();

const loading = ref(false);
const loadingCases = ref(false);
const settingUpAccess = ref(false);
const selectedCaseId = ref<string | null>(null);
const personalDataspaceId = ref<string | null>(null);

const cases = ref<{ botId: string; botName: string }[]>([]);

// Access control
const showAccessSnackbar = ref(false);
const accessSnackbarMessage = ref("");
const accessSnackbarType = ref<"success" | "warning" | "error">("success");

const hasEditorAccess = computed(() => {
	return domainStore.hasAccessRights(UserRole.Editor) || domainStore.hasAccessRights(UserRole.Admin);
});

function getCaseDescription(botId: string): string {
	const descriptions: Record<string, string> = {
		"Fall 1: Henning Müller":
			"Technikbegeistert, aber planlos: Henning steht vor dem Abschluss und sucht Orientierung für einen Beruf mit Zukunft.",
		"Fall 2: Leonie Weber":
			"Viele Möglichkeiten, keine Richtung: Leonie will herausfinden, welcher Beruf wirklich zu ihr passt – und wo sie anfangen soll.",
		"Fall 3: Nina Kalenko":
			"Ziel klar, Weg ungewiss: Nina träumt von einem Neurologiestudium und sucht realistische Wege, ihren Wunsch wahrzumachen.",
	};

	return descriptions[botId] || "Ein interessanter Beratungsfall zum Üben Ihrer Gesprächsführung.";
}

async function adminOperation(operation: string, params: any) {
	const response = await fetch("/api/berufsberatung/admin-operation", {
		method: "POST",
		headers: {
			"Content-Type": "application/json",
		},
		body: JSON.stringify({ operation, ...params }),
	});

	if (!response.ok) {
		throw new Error(`HTTP error! status: ${response.status}`);
	}

	return response.json();
}

async function hasAccess(userId: string): Promise<{ access: boolean; dataspaces: string[] }> {
	try {
		const result = await adminOperation("checkAccess", { domainId: DOMAIN_ID, userId });
		const member = result.member;
		const accessibleDataspaces = result.accessibleDataspaces || [];
		console.log("Accessible Dataspaces:", accessibleDataspaces);
		const access = member.operator !== "none" && member.operator !== "viewer";
		return { access, dataspaces: accessibleDataspaces.map((ds: any) => ds.id) };
	} catch (error) {
		console.error("Error checking access:", error);
		return { access: false, dataspaces: [] };
	}
}

async function patchDomainAccess(userId: string): Promise<any> {
	try {
		const result = await adminOperation("patchDomainAccess", { domainId: DOMAIN_ID, userId });
		return result.member;
	} catch (error) {
		console.error("Error patching access:", error);
		throw error;
	}
}
async function patchDataspaceAccess(instanceDataspaceId: string, userId: string): Promise<any> {
	try {
		const result = await adminOperation("patchDataspaceAccess", {
			domainId: DOMAIN_ID,
			instanceDataspaceId,
			userId,
		});
		return result.member;
	} catch (error) {
		console.error("Error patching access:", error);
		throw error;
	}
}

async function getLatestDataspaceVersion(): Promise<string> {
	try {
		const domain = await operatorAPI().domain(DOMAIN_ID).get();
		const versions = (domain.versions || []).filter((v: string) => v !== "wip");
		if (versions.length === 0) return FALLBACK_DATASPACE_VERSION;
		return versions[versions.length - 1];
	} catch (error) {
		console.error("Error determining latest dataspace version, using fallback:", error);
		return FALLBACK_DATASPACE_VERSION;
	}
}

async function createPersonalDataspace(userId: string, label: string, description: string): Promise<string> {
	return new Promise(async (resolve) => {
		await instanceDataspacesStore.loadDataspaces(DOMAIN_ID);
		const version = await getLatestDataspaceVersion();
		await instanceDataspacesStore
			.addDataspace(TEMPALTE_DATASPACE_ID, version, label, description, true)
			.then(async (d) => {
				console.log("Created personal dataspace:", d);
				if (!d.dataspace) return;
				const dataspaceId = d.dataspace.id;
				try {
					await instanceDataspacesStore.setAllowAll(DOMAIN_ID, dataspaceId, false);
					await instanceDataspacesStore.addPermittedUserId(DOMAIN_ID, dataspaceId, userId);
				} catch (error) {
					console.error("Error saving permitted users:", error);
					resolve("");
				}
				resolve(dataspaceId);
			})
			.catch((error) => {
				console.error("Error creating personal dataspace:", error);
				resolve("");
			});
	});
}

async function checkAndSetupAccess() {
	try {
		settingUpAccess.value = true;

		console.log("DEBUG");
		console.log(userStore.currentUser);

		if (!userStore.currentUser || !userStore.currentUser.id) {
			accessSnackbarMessage.value = "Fehler: Benutzer nicht gefunden.";
			accessSnackbarType.value = "error";
			showAccessSnackbar.value = true;
			return;
		}

		const hasEditorAccess = await hasAccess(userStore.currentUser.id);

		if (hasEditorAccess.access) {
			// Create personal dataspace if none exists
			if (hasEditorAccess.dataspaces.length === 0) {
				// User has no dataspaces, create a personal one
				accessSnackbarMessage.value = "Erstelle persönlichen Arbeitsbereich...";
				accessSnackbarType.value = "warning";
				showAccessSnackbar.value = true;

				personalDataspaceId.value = await createPersonalDataspace(
					userStore.currentUser.id,
					`${userStore.currentUser.firstname} ${userStore.currentUser.lastname}`,
					"Persönlicher Arbeitsbereich für Berufsberatung Fall-Beichspiele",
				);
				console.log("Created personal dataspace:", personalDataspaceId.value);
			} else {
				personalDataspaceId.value = hasEditorAccess.dataspaces[0];
				console.log("Using existing dataspace:", personalDataspaceId.value);
			}

			await domainStore.loadDomain(DOMAIN_ID);
			accessSnackbarMessage.value = "Zugang bestätigt!";
			accessSnackbarType.value = "success";
			showAccessSnackbar.value = true;
			return;
		}

		// User doesn't have access, add them as editor
		accessSnackbarMessage.value = "Bereite Editor-Zugang vor...";
		accessSnackbarType.value = "warning";
		showAccessSnackbar.value = true;

		try {
			await patchDomainAccess(userStore.currentUser.id);
			await new Promise((resolve) => setTimeout(resolve, 5_000));

			// User has no dataspaces, create a personal one
			accessSnackbarMessage.value = "Erstelle persönlichen Arbeitsbereich...";
			accessSnackbarType.value = "warning";
			showAccessSnackbar.value = true;
			personalDataspaceId.value = await createPersonalDataspace(
				userStore.currentUser.id,
				`${userStore.currentUser.firstname} ${userStore.currentUser.lastname}`,
				"Persönlicher Arbeitsbereich für Berufsberatung Fall-Beichspiele",
			);
			console.log("Created personal dataspace:", personalDataspaceId.value);

			await patchDataspaceAccess(personalDataspaceId.value, userStore.currentUser.id);

			// Wait 5 seconds after successful patchAccess
			await new Promise((resolve) => setTimeout(resolve, 5_000));
			// Refresh members (manual fetch due to admin token use, otherwise maybe 401 unauthorized)
			await adminOperation("refreshMembers", { domainId: DOMAIN_ID });
			// Wait 5 seconds after refreshing members
			await new Promise((resolve) => setTimeout(resolve, 5_000));

			await domainStore.loadDomain(DOMAIN_ID);

			if (domainStore.hasAccessRights(UserRole.Editor)) {
				accessSnackbarMessage.value = "Editor-Zugang erfolgreich eingerichtet!";
				accessSnackbarType.value = "success";
			} else {
				accessSnackbarMessage.value = "Zugang konnte nicht eingerichtet werden.";
				accessSnackbarType.value = "error";
			}
		} catch (patchError) {
			console.error("Error patching access:", patchError);
			accessSnackbarMessage.value = "Fehler beim Einrichten des Zugangs.";
			accessSnackbarType.value = "error";
		}

		showAccessSnackbar.value = true;
	} catch (error) {
		console.error("Error setting up access:", error);
		accessSnackbarMessage.value = "Fehler beim Überprüfen des Zugangs.";
		accessSnackbarType.value = "error";
		showAccessSnackbar.value = true;
	} finally {
		settingUpAccess.value = false;
	}
}

async function loadCases() {
	if (!hasEditorAccess.value) return;

	try {
		loadingCases.value = true;
		const bots = await alternisAPI().bots().get(domainStore.account.id, domainStore.domain.id);
		cases.value = bots.sort((a, b) => a.botName.localeCompare(b.botName));
	} catch (error) {
		console.error("Error loading cases:", error);
	} finally {
		loadingCases.value = false;
	}
}

async function selectCase(case_: any, instanceDataspaceId: string) {
	if (loading.value || !hasEditorAccess.value) return;

	loading.value = true;
	selectedCaseId.value = case_.botId;

	try {
		const workspace = await instanceWorkspacesStore.addWorkspace(
			instanceDataspaceId,
			TEMPLATE_WORKSPACE_ID,
			case_.botName,
			`Berufsberatungsfall erstellt am ${new Date().toLocaleString("de-DE", { timeZone: "Europe/Berlin" })}`,
		);

		await instanceWorkspacesStore.setWorkspaceDescriptor(
			DOMAIN_ID,
			instanceDataspaceId,
			workspace.id,
			case_.botName,
			`${domainStore.account.id} - ${new Date().toLocaleString("de-DE", { timeZone: "Europe/Berlin" })}`,
		);

		// Add to dataspace
		const dataspace = await instanceDataspacesStore.getById(instanceDataspaceId);
		if (dataspace) {
			dataspace.workspaces.push(workspace.id);
			await instanceDataspacesStore.updateDataspace(dataspace);
		}

		// Redirect with bot parameter
		router.push({
			path: `/${DOMAIN_ID}/template/${instanceDataspaceId}/template/${workspace.id}`,
			query: { botId: case_.botId },
		});
	} catch (error) {
		console.error("Error creating workspace:", error);
	} finally {
		loading.value = false;
		selectedCaseId.value = null;
	}
}

async function loadBerufsberatungData() {
	try {
		await instanceDataspacesStore.loadDataspaces(DOMAIN_ID);
		if (hasEditorAccess.value) {
			await loadCases();
		}
	} catch (error) {
		console.error("Error loading Berufsberatung data:", error);
		loadingCases.value = false;
	}
}

onMounted(async () => {
	document.body.classList.add("berufsberatung-page");
	document.body.style.backgroundColor = "rgb(128, 203, 196)";

	await checkAndSetupAccess();

	if (hasEditorAccess.value) {
		await loadBerufsberatungData();
	}
});

onUnmounted(() => {
	document.body.classList.remove("berufsberatung-page");
	document.body.style.backgroundColor = "";
});
</script>

<style scoped lang="scss">
.landing-page {
	min-height: 100vh;
	background-color: #1a1a1a;
}

.header-row {
	margin-top: -30px;
	margin-bottom: -30px;
	@media screen and (max-width: 690px) {
		margin-top: -70px;
		margin-bottom: -50px;
	}
}

.headline {
	font-size: 3rem;
	font-family: "STEPS", sans-serif;
	color: #ffffff;
	text-shadow: 2px 2px 1px rgba(0, 0, 0, 0.5);
	@media screen and (max-width: 690px) {
		font-size: 2rem;
	}
}

.header-text {
	font-size: 1.25rem;
	color: #fff;
	text-shadow: 2px 2px 1px rgba(0, 0, 0, 0.5);
	@media screen and (max-width: 690px) {
		font-size: 1rem;
	}
}

.header-div {
	margin: 100px 3rem 100px;
	@media screen and (max-width: 1200px) {
		margin: 100px 2rem 50px;
	}
	@media screen and (max-width: 690px) {
		margin: 100px 1.75rem 50px;
	}
}

.header-content {
	display: flex;
	height: 100%;
	justify-content: center;
	flex-direction: column;
	text-shadow: 0px 0px 10px rgba(0, 0, 0, 0.5);
}

.section-div {
	margin: 60px 3rem 50px;
	@media screen and (max-width: 1200px) {
		margin: 50px 2rem 50px;
	}
	@media screen and (max-width: 690px) {
		margin: 45px 1.75rem 40px;
	}
}

.title {
	color: #80cbc4;
	font-size: 3rem;
	text-align: center;
	width: 100%;
	margin-bottom: 2rem;
	text-shadow: 1px 1px 1px rgba(0, 0, 0, 0.5);
	@media screen and (max-width: 1200px) {
		font-size: 2.3rem;
		white-space: normal;
	}
	@media screen and (max-width: 690px) {
		font-size: 2rem;
	}
}

.loading-section {
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
	min-height: 200px;

	.loading-text {
		color: #80cbc4;
		font-size: 1.1rem;
		margin-top: 1rem;
		text-shadow: 0px 0px 1px rgba(0, 0, 0, 0.3);
	}
}

.no-cases-section {
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
	min-height: 200px;

	.no-cases-text {
		color: #80cbc4;
		font-size: 1.1rem;
		text-shadow: 0px 0px 1px rgba(0, 0, 0, 0.3);
	}
}

.cases-row {
	margin-bottom: 2rem;
}

.case-card {
	border-left: 0.8rem solid #80cbc4;
	padding: 1rem 1rem;
	height: 100%;
	cursor: pointer;
	transition: transform 0.3s ease, box-shadow 0.3s ease;

	&:hover:not([disabled]) {
		transform: translateY(-4px);
		box-shadow: 0 8px 16px rgba(128, 203, 196, 0.3);
	}

	&[disabled] {
		opacity: 0.7;
		cursor: not-allowed;
	}
}

.case-title {
	color: #80cbc4;
	font-size: 1.3rem;
	@media screen and (max-width: 690px) {
		line-height: 80%;
		font-size: 1.1rem;
		max-width: 100%;
		white-space: pre-wrap;
		word-break: break-word;
	}
}

.case-text {
	font-size: 1rem;
	line-height: 1.5;
}

.case-icon-wrapper {
	display: flex;
	justify-content: center;
	margin-bottom: 1rem;
}

.case-actions {
	padding-top: 1rem;
}

.case-button {
	font-weight: 600;
	color: white;
}

.description-section {
	margin: 2rem 0;
	text-align: center;

	.description-text {
		color: #80cbc4;
		font-size: 1.1rem;
		line-height: 1.6;
		text-shadow: 0px 0px 1px rgba(0, 0, 0, 0.3);
		max-width: 1000px;
		margin: 0 auto;
		text-align: justify;

		@media screen and (max-width: 690px) {
			font-size: 1rem;
			text-align: left;
		}
	}
}

.logos-section {
	display: flex;
	justify-content: center;
	align-items: center;
	gap: 4rem;
	margin: 2rem 0;

	@media screen and (max-width: 768px) {
		flex-direction: column;
		gap: 2rem;
	}

	.logo-container {
		display: flex;
		align-items: center;
		justify-content: center;
		background-color: white;
		border-radius: 10px;
		padding: 15px;
		width: 250px;
		height: 120px;
		box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);

		@media screen and (max-width: 690px) {
			width: 200px;
			height: 100px;
			padding: 10px;
		}

		&.hdba-container {
			gap: 0.3rem;
		}

		.logo {
			max-height: 70px;
			height: auto;
			transition: filter 0.3s ease;

			&.dfki-logo {
				max-width: 200px;
				max-height: 80px;
			}

			&.hdba-logo {
				max-height: 50px;
			}

			@media screen and (max-width: 690px) {
				max-height: 60px;

				&.dfki-logo {
					max-width: 160px;
					max-height: 70px;
				}

				&.hdba-logo {
					max-height: 40px;
				}
			}
		}
	}
}

@media (max-width: 768px) {
	.header-div {
		margin: 50px 1rem 50px;
	}

	.section-div {
		margin: 30px 1rem 30px;
	}
}

.nonSelect {
	-webkit-touch-callout: none;
	-webkit-user-select: none;
	-khtml-user-select: none;
	-moz-user-select: none;
	-ms-user-select: none;
	user-select: none;
}

svg {
	.affected_person_tag_fill {
		fill: #f4f4f4;
	}

	&.dark {
		.affected_person_tag_fill {
			fill: #404040;
		}
	}
}
</style>
